/**
 * Centralized documentation URLs for the Kapa.ai documentation site.
 * All external documentation links should be imported from this file.
 */

export const DOCS_BASE_URL = 'https://docs.kapa.ai';

/** Kapa.ai customer support portal. */
export const SUPPORT_PORTAL_URL = 'https://support.kapa.ai';

export const DOCS_URLS = {
  useCases: {
    inProductAgents: `${DOCS_BASE_URL}/use-cases/in-product-agents`,
    documentationAssistant: `${DOCS_BASE_URL}/use-cases/documentation-assistant`,
    internalKnowledge: `${DOCS_BASE_URL}/use-cases/internal-knowledge`,
    publicMcpServer: `${DOCS_BASE_URL}/use-cases/public-mcp-server`,
    supportWorkflows: `${DOCS_BASE_URL}/use-cases/support-workflows`,
    answerRfps: `${DOCS_BASE_URL}/use-cases/answer-rfps`,
  },
  // Getting Started
  gettingStarted: {
    quickstart: `${DOCS_BASE_URL}/getting-started/index-your-first-source`,
  },

  // Examples
  examples: {
    base: `${DOCS_BASE_URL}/examples/`,
  },

  // Knowledge Sources
  knowledgeSources: {
    base: `${DOCS_BASE_URL}/knowledge-sources/`,
  },

  // Retrieval
  retrieval: {
    base: `${DOCS_BASE_URL}/retrieval/`,
    chooseMode: `${DOCS_BASE_URL}/retrieval/guides/tuning-knowledge-base-search`,
  },

  // Account & Authentication
  authentication: {
    base: `${DOCS_BASE_URL}/account/authentication`,
    oauth: `${DOCS_BASE_URL}/account/authentication#oauth`,
    saml: `${DOCS_BASE_URL}/account/authentication#saml-based-sso`,
    otp: `${DOCS_BASE_URL}/account/authentication#one-time-password`,
  },

  // API
  api: {
    overview: `${DOCS_BASE_URL}/api/overview`,
    reference: `${DOCS_BASE_URL}/api/reference`,
    quickstart: `${DOCS_BASE_URL}/api/quickstart`,
    chat: `${DOCS_BASE_URL}/api/reference/query-v-1-projects-chat`,
  },

  // Analytics
  analytics: {
    customTags: `${DOCS_BASE_URL}/analytics/custom-tags`,
    coverageGapsWorkflow: `${DOCS_BASE_URL}/analytics/coverage-gaps#recommended-workflow`,
    topQuestionsWorkflow: `${DOCS_BASE_URL}/analytics/top-questions#recommended-workflow`,
    sourceAnalytics: `${DOCS_BASE_URL}/analytics/source-analytics`,
  },

  // Data Sources
  dataSources: {
    overview: `${DOCS_BASE_URL}/data-sources/overview`,
    manage: `${DOCS_BASE_URL}/data-sources/manage`,
    confluence: `${DOCS_BASE_URL}/data-sources/confluence`,
    customQa: `${DOCS_BASE_URL}/data-sources/custom-qa`,
    discord: `${DOCS_BASE_URL}/data-sources/discord`,
    discourse: `${DOCS_BASE_URL}/data-sources/discourse`,
    fileUpload: `${DOCS_BASE_URL}/data-sources/file-upload`,
    githubDiscussions: `${DOCS_BASE_URL}/data-sources/github-discussions`,
    githubCode: `${DOCS_BASE_URL}/data-sources/github-code`,
    githubFiles: `${DOCS_BASE_URL}/data-sources/github-files`,
    githubIssues: `${DOCS_BASE_URL}/data-sources/github-issues`,
    githubPullRequests: `${DOCS_BASE_URL}/data-sources/github-pull-requests`,
    jira: `${DOCS_BASE_URL}/data-sources/jira`,
    jiraServiceManagement: `${DOCS_BASE_URL}/data-sources/jira-service-management`,
    notion: `${DOCS_BASE_URL}/data-sources/notion`,
    openapi: `${DOCS_BASE_URL}/data-sources/openapi`,
    s3: `${DOCS_BASE_URL}/data-sources/s3-storage`,
    salesforceCases: `${DOCS_BASE_URL}/data-sources/salesforce-cases`,
    salesforceKnowledge: `${DOCS_BASE_URL}/data-sources/salesforce-knowledge`,
    slack: `${DOCS_BASE_URL}/data-sources/slack`,
    webCrawling: `${DOCS_BASE_URL}/data-sources/web-crawling`,
    youtube: `${DOCS_BASE_URL}/data-sources/youtube`,
    googleDrive: `${DOCS_BASE_URL}/data-sources/google-drive`,
    zendeskHelpCenter: `${DOCS_BASE_URL}/data-sources/zendesk-help-center`,
    zendeskTickets: `${DOCS_BASE_URL}/data-sources/zendesk-support-tickets`,
  },

  // Integrations
  integrations: {
    base: `${DOCS_BASE_URL}/integrations/`,
    mcp: `${DOCS_BASE_URL}/integrations/mcp/overview`,
    mcpToolCustomization: `${DOCS_BASE_URL}/integrations/mcp/overview#customizing-the-tool-name-and-description-advanced`,
    slackBot: `${DOCS_BASE_URL}/integrations/slack-bot`,
    slackBotInstall: `${DOCS_BASE_URL}/integrations/slack-bot#4-install-kapa-slack-bot-to-your-workspace`,
    discordBot: `${DOCS_BASE_URL}/integrations/discord-bot`,
    discordBotInstall: `${DOCS_BASE_URL}/integrations/discord-bot#4-install-kapa-discord-bot-to-your-server`,
    supportFormDeflector: `${DOCS_BASE_URL}/integrations/support-form-deflector`,
    widget: `${DOCS_BASE_URL}/integrations/website-widget`,
    zendeskAgent: `${DOCS_BASE_URL}/integrations/zendesk-agent`,
    chatSdk: `${DOCS_BASE_URL}/dev/sdk`,
    agentSdk: `${DOCS_BASE_URL}/dev/agent`,
    widgetSatisfaction: `${DOCS_BASE_URL}/integrations/website-widget/features/user-satisfaction`,
    widgetUserTracking: `${DOCS_BASE_URL}/integrations/website-widget/features/user-tracking`,
    widgetCustomMetadata: `${DOCS_BASE_URL}/integrations/website-widget/features/user-tracking#custom-user-metadata`,
  },

  // Customizations
  customizations: {
    overview: `${DOCS_BASE_URL}/customizing`,
  },

  // Improving & Troubleshooting
  improving: {
    troubleshooting: `${DOCS_BASE_URL}/improving/troubleshooting-improving-answers#step-3-use-improve-this-answer-for-specific-cases`,
  },
} as const;
